import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardText, CardFooter } from 'reactstrap';
import PropTypes from 'prop-types';
import DentallyDetailModal from './dentallyDetailModal';
import DeleteModal from './DeleteModal';
import {
  getDentallyConnection,
  disconnectDentally,
  toggleDentallyStatus
} from '../../axios/dentally/index.api';
import { useAuthStore } from '../../zustand-store/auth.store';

const DentallyCard = ({ title, description, icon }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDentallyIntegrated, setIsDentallyIntegrated] = useState(false);
  const [dentallyToken, setDentallyToken] = useState('');
  const [dentallyLocationId, setDentallyLocationId] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [integrationStats, setIntegrationStats] = useState({
    hasToken: false,
    hasSite: false,
    hasPractitioner: false,
    hasServices: false,
    hasPaymentPlan: false
  });

  const locationId = useAuthStore((state) => state.locationId);

  const toggleModal = () => setModalOpen((prev) => !prev);

  const handleToggleStatus = async () => {
    if (!dentallyLocationId) return;
    try {
      setLoading(true);
      await toggleDentallyStatus(locationId, dentallyLocationId, !isActive);
      setIsActive((prev) => !prev);
    } catch (err) {
      console.error('Dentally toggle status error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await disconnectDentally(locationId);
      setIsDentallyIntegrated(false);
      setDentallyToken('');
      setIntegrationStats({
        hasToken: false,
        hasSite: false,
        hasPractitioner: false,
        hasServices: false,
        hasPaymentPlan: false
      });
      setDisconnectModalOpen(false);
    } catch (err) {
      console.error('Dentally disconnect error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDentallyStatus = async () => {
    if (!locationId) return;

    try {
      setLoading(true);
      const res = await getDentallyConnection(locationId);

      if (res?.data?.code === 0 && res?.data?.data) {
        const data = res.data.data;
        const token = data.dentallyToken || '';
        const siteId = data.dentallySiteId;
        const practitionerId = data.dentallyPractionerId;
        const paymentPlanId = data.paymentPlanId;

        setDentallyToken(token);
        setDentallyLocationId(data.id || null);
        setIsActive(data.isActive || false);

        const stats = {
          hasToken: !!token,
          hasSite: !!siteId,
          hasPractitioner: !!practitionerId,
          hasServices: data.count > 0 ? true : false,
          hasPaymentPlan: !!paymentPlanId
        };
        // if (data.dentallyServices && data.dentallyServices.length > 0) {
        //   const mappedServices = data.dentallyServices.filter(
        //     (s) => s.dentallyServiceId || s.serviceLinkId
        //   );
        //   stats.hasServices = mappedServices.length > 0;
        // }

        setIntegrationStats(stats);

        const countDone = [
          !!token,
          !!siteId,
          !!practitionerId,
          stats.hasServices,
          !!paymentPlanId
        ].filter(Boolean).length;
        const isFullyIntegrated = countDone === 5;
        setIsDentallyIntegrated(isFullyIntegrated);
      }
    } catch (err) {
      console.error('Dentally status error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDentallyStatus();
  }, [locationId, modalOpen]);

  return (
    <>
      <Card
        onClick={toggleModal}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          cursor: 'pointer',
          height: 250,
          transition: 'all 0.2s ease'
        }}
        className="shadow-sm hover-shadow">
        <CardBody className="d-flex flex-column ">
          <div className="d-flex justify-content-between">
            <img src={icon} alt="dentally" style={{ width: 32, height: 32 }} />

            {loading ? (
              <span className="text-warning" style={{ fontSize: 13 }}>
                Loading...
              </span>
            ) : (
              <>
                {!isDentallyIntegrated && !integrationStats.hasToken ? (
                  <span className="text-primary">Connect</span>
                ) : (
                  <span
                    className={`${
                      isDentallyIntegrated
                        ? 'bg-success'
                        : integrationStats.hasToken
                          ? 'bg-warning'
                          : 'bg-secondary'
                    } text-white d-flex align-items-center`}
                    style={{
                      fontSize: 12,
                      padding: '4px 12px',
                      borderRadius: 50,
                      gap: 5
                    }}>
                    <i className="mdi mdi-check" />
                    {isDentallyIntegrated
                      ? 'Connected'
                      : integrationStats.hasToken
                        ? 'Partial'
                        : 'Connect'}
                  </span>
                )}
              </>
            )}
          </div>

          <div>
            <p
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#111827',
                marginTop: 16,
                marginBottom: 6
              }}>
              {title}
            </p>

            <CardText style={{ color: '#000', fontSize: '13px', fontWeight: '400', marginTop: 8 }}>
              {description}
            </CardText>
          </div>

          {integrationStats.hasToken && (
            <div className="mt-auto pt-2">
              <p className="m-0 p-0 text-black mb-2">Configuration status</p>
              <div className="d-flex flex-wrap gap-1">
                <span
                  className={`badge ${integrationStats.hasSite ? 'bg-success' : 'bg-secondary'}`}
                  style={{ fontSize: 10 }}>
                  Site: {integrationStats.hasSite ? 'Done' : 'Pending'}
                </span>
                <span
                  className={`badge ${
                    integrationStats.hasPractitioner ? 'bg-success' : 'bg-secondary'
                  }`}
                  style={{ fontSize: 10 }}>
                  Practitioner: {integrationStats.hasPractitioner ? 'Done' : 'Pending'}
                </span>
                <span
                  className={`badge ${
                    integrationStats.hasServices ? 'bg-success' : 'bg-secondary'
                  }`}
                  style={{ fontSize: 10 }}>
                  Services: {integrationStats.hasServices ? 'Done' : 'Pending'}
                </span>
                <span
                  className={`badge ${
                    integrationStats.hasPaymentPlan ? 'bg-success' : 'bg-secondary'
                  }`}
                  style={{ fontSize: 10 }}>
                  Payment Plan: {integrationStats.hasPaymentPlan ? 'Done' : 'Pending'}
                </span>
              </div>
            </div>
          )}
        </CardBody>
        <CardFooter
          style={{ minHeight: '48px' }}
          className="d-flex align-items-center justify-content-end py-2"
          onClick={(e) => e.stopPropagation()}>
          {integrationStats.hasToken ? (
            loading ? (
              <span className="text-warning mb-0">Loading...</span>
            ) : (
              <div className="d-flex align-items-center gap-2" style={{ gap: 12 }}>
                {isDentallyIntegrated && (
                  <>
                    <span
                      style={{ cursor: 'pointer', fontSize: 13 }}
                      className={isActive ? 'text-warning' : 'text-success'}
                      onClick={handleToggleStatus}>
                      {isActive ? 'Set Inactive' : 'Set Active'}
                    </span>
                    <span style={{ color: '#dee2e6' }}>|</span>
                  </>
                )}
                <span
                  className="text-danger"
                  style={{ cursor: 'pointer', fontSize: 13 }}
                  onClick={() => setDisconnectModalOpen(true)}>
                  Disconnect
                </span>
              </div>
            )
          ) : (
            <span className="invisible">Disconnect</span>
          )}
        </CardFooter>
      </Card>

      <DentallyDetailModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title={title}
        icon={icon}
        description={description}
        isIntegrated={isDentallyIntegrated}
        dentallyToken={dentallyToken}
        refreshStatus={fetchDentallyStatus}
      />
      <DeleteModal
        show={disconnectModalOpen}
        isDeleting={loading}
        heading="Are you sure you want to disconnect Dentally? Your services, practitioner, and payment plan mappings will need to be reconfigured if you reconnect."
        componentName="Dentally"
        buttonTitle="Disconnect"
        onDeleteClick={handleDisconnect}
        onCloseClick={() => setDisconnectModalOpen(false)}
      />
    </>
  );
};

DentallyCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired
};

export default DentallyCard;
